/**
 * Lightweight wrapper around Google Analytics 4 (gtag.js).
 *
 * GA4 measurement ID is loaded in index.html. This module:
 *   - Exposes typed helpers for pageviews + custom events
 *   - Fires the GA4 recommended `generate_lead` conversion event for form submissions
 *   - Buffers calls if gtag hasn't loaded yet (resilient to script async loading)
 *
 * Mark `generate_lead` as a Conversion in GA4 Admin > Events to track in Search Console
 * + Google Ads. Search Console rankings/CTR are tracked automatically once the property
 * is verified (see <meta name="google-site-verification"> in index.html).
 */

export const GA_MEASUREMENT_ID = "G-L6QPP0YYTP";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type GtagArgs = unknown[];

function callGtag(...args: GtagArgs) {
  if (typeof window === "undefined") return;
  // Always push to dataLayer so events queue even before gtag.js finishes loading.
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === "function") {
    window.gtag(...(args as Parameters<NonNullable<Window["gtag"]>>));
  } else {
    window.dataLayer.push(args);
  }
}

/** Manual SPA pageview — call on every route change. */
export function trackPageview(path: string, title?: string) {
  callGtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

/** Generic custom event. Use snake_case event names per GA4 conventions. */
export function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {},
) {
  callGtag("event", eventName, params);
}

/**
 * Track a lead/form submission as a GA4 conversion.
 *
 * Mark `generate_lead` as a Conversion in GA4 Admin so it counts toward
 * Google Ads conversions and Search Console attribution.
 */
export function trackLead(params: {
  formType: string;
  /** Estimated lead value in GBP — helps optimise ROAS in GA4 / Google Ads. */
  value?: number;
  currency?: string;
  service?: string;
  source?: string;
  [k: string]: unknown;
}) {
  const { value = 250, currency = "GBP", ...rest } = params;
  const utm = readUTMParamsSafe();
  trackEvent("generate_lead", {
    value,
    currency,
    ...utm,
    ...rest,
  });
  // Also fire a generic form_submit event for funnel analysis.
  trackEvent("form_submit", { ...utm, ...rest, value, currency });
}

// Forward declaration helper — readUTMParams defined below; safe wrapper avoids TDZ.
function readUTMParamsSafe(): Record<string, string> {
  try {
    return readUTMParams() as Record<string, string>;
  } catch {
    return {};
  }
}

/** CTA click — useful for measuring button effectiveness across the site. */
export function trackCTAClick(label: string, location: string) {
  trackEvent("cta_click", { cta_label: label, cta_location: location });
}

// ============================================================================
// TRACKING PLAN — UTM + Conversion Events
// ----------------------------------------------------------------------------
// Conversion events (mark as Conversions in GA4 Admin → Events):
//   • generate_lead       — any form_submission (lead form, lead magnet)
//   • pdf_download        — Search Visibility audit / upsell / exec PDF download
//   • booking_complete    — Calendly invitee.created (call booked)
//
// Supporting events:
//   • page_view, cta_click, form_submit, form_start, vsl_play, scroll_*
//
// UTM source-of-truth: useUTMTracking hook persists utm_* + gclid/fbclid in
// sessionStorage. Helpers below read from URL → sessionStorage so every
// conversion event is enriched with attribution params automatically.
// ============================================================================

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
] as const;

export type UTMRecord = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/** Read UTM params from current URL, falling back to persisted sessionStorage. */
export function readUTMParams(): UTMRecord {
  if (typeof window === "undefined") return {};
  const out: UTMRecord = {};
  const url = new URLSearchParams(window.location.search);
  let stored: Record<string, string | null> = {};
  try {
    stored = JSON.parse(sessionStorage.getItem("vgp_utm_params") || "{}");
  } catch {
    /* ignore */
  }
  for (const k of UTM_KEYS) {
    const v = url.get(k) ?? stored[k] ?? null;
    if (v) out[k] = v;
  }
  return out;
}

/** Append the current session's UTM params to an outbound URL (Calendly, etc). */
export function withUTMParams(url: string): string {
  const utm = readUTMParams();
  if (!Object.keys(utm).length) return url;
  try {
    const u = new URL(url, window.location.origin);
    for (const [k, v] of Object.entries(utm)) {
      if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch {
    return url;
  }
}

/** PDF download conversion — fires for the Search Visibility lead magnet PDFs. */
export function trackPdfDownload(params: {
  /** e.g. "search_visibility_audit" | "upsell" | "exec_summary" */
  pdfType: string;
  /** Email captured on the gate form, when available. */
  email?: string;
  /** Estimated value in GBP — keep modest for ROAS modeling. */
  value?: number;
}) {
  const { pdfType, email, value = 50 } = params;
  trackEvent("pdf_download", {
    pdf_type: pdfType,
    email,
    value,
    currency: "GBP",
    ...readUTMParams(),
  });
  // Also fire generate_lead so PDF gates contribute to the lead conversion.
  trackEvent("generate_lead", {
    form_type: `pdf_${pdfType}`,
    value,
    currency: "GBP",
    ...readUTMParams(),
  });
}

/** Calendly booking confirmed — primary booking conversion event. */
export function trackBookingComplete(params: {
  /** Where the booking happened: "/book", "/thank-you-funnel", etc. */
  location?: string;
  /** Estimated booked-call value in GBP. */
  value?: number;
  /** Calendly scheduled event URI (e.g. https://api.calendly.com/scheduled_events/UUID). */
  eventUri?: string;
  /** Calendly invitee URI (e.g. .../scheduled_events/UUID/invitees/UUID). */
  inviteeUri?: string;
} = {}) {
  const {
    location = window.location.pathname,
    value = 500,
    eventUri,
    inviteeUri,
  } = params;

  const lastSegment = (uri?: string) =>
    uri ? uri.split("/").filter(Boolean).pop() : undefined;

  const eventId = lastSegment(eventUri);
  const inviteeId = lastSegment(inviteeUri);
  // Stable join key for dedupe + lead↔conversion linking.
  const transactionId = eventId && inviteeId ? `${eventId}:${inviteeId}` : eventId || inviteeId;

  trackEvent("booking_complete", {
    booking_location: location,
    value,
    currency: "GBP",
    calendly_event_id: eventId,
    calendly_invitee_id: inviteeId,
    calendly_event_uri: eventUri,
    calendly_invitee_uri: inviteeUri,
    transaction_id: transactionId,
    ...readUTMParams(),
  });
}

